/**
 * One click, one order — ROADMAP 4.15.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THERE IS NO CONFIRMATION, ON ANY MODE. THIS IS A DELIBERATE CHOICE.
 *
 * B and S in the ladder send immediately, at the lots set in the header, on
 * PAPER and on LIVE alike. The ticket bar and the typed-LIVE word are gone.
 * The owner asked for this explicitly: a trader clicking a strike during a
 * move cannot be made to read a dialog, and every competing terminal fires
 * on the click.
 *
 * WHAT THAT COSTS, STATED ONCE SO NOBODY REDISCOVERS IT IN PRODUCTION: a
 * misclick on a LIVE account is a real position, immediately, with no undo.
 * The remaining protections are the ones the ORDER PATH enforces and a
 * click cannot skip — the feed must be live (§4.11), the account must be
 * chosen, lots are bounded, and every order carries an idempotency key, so
 * a double click cannot become two positions. The strategy basket keeps its
 * own pause: the checklist in `StrategyPicker`.
 *
 * REFUSED BEFORE SENDING, NOT AFTER. The three reasons an order cannot go —
 * no account, no size, no instrument — are checked here and answered in a
 * sentence, because the alternative is a round trip that ends in a broker
 * message nobody can act on.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import {
  isOrderWindow,
  type BrokerConnectionDto,
  type OrderIntentRequest,
  type OrderSideValue,
  type PremiumOrderRequest,
} from '@apna/shared'
import { tickSocket } from '@/feed/tickSocket'
import { orderStore } from '@/store/orders'

export interface LegOrder {
  token: number
  side: OrderSideValue
  lots: number
}

/**
 * What ONE click sends: one intent, one idempotency key, one or more legs.
 *
 * The label sits here rather than on a leg because there is one of it per
 * ORDER — it is what the toast and the order drawer show for the whole
 * request. A straddle placed from the chain (4.18) is two legs under one
 * label, and `openTrade` turns exactly that into one Trade with two legs.
 */
export interface ClickOrder {
  readonly legs: readonly LegOrder[]
  /** For the label a person reads in the toast and the order drawer. */
  readonly label: string
  /** A ticket keeps this stable when an acknowledgement is lost. */
  readonly clientRequestId?: string
  readonly premiumOrder?: PremiumOrderRequest
}

export interface PlaceResult {
  ok: boolean
  /** Why it was refused. Null when it was accepted. */
  reason: string | null
}

const makeRequestId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `req-${Date.now()}-${Math.random().toString(36).slice(2)}`

/**
 * Send one click's order and report what happened.
 *
 * ONE INTENT, however many legs. The whole point of sending a straddle as a
 * basket rather than two clicks is that both legs share one
 * `clientRequestId`, so they get one idempotency key, one Trade and one
 * all-or-nothing write — a double-click cannot half-open it, and a crash
 * between two separate sends cannot leave one side naked.
 *
 * Never throws: the caller is a click handler, and an unhandled rejection
 * in one would leave the trader with no answer at all.
 */
/**
 * The three fields an order needs from an account — ROADMAP 9.61.
 *
 * Narrowed from the whole `BrokerConnectionDto` because that is all this
 * reads, and the wider type kept a caller out that had every field it
 * actually uses: the Positions drawer shows every account at once and
 * holds a trade's broker as an id, a label and a mode, not as the full
 * row the header's selector carries. A parameter should ask for what it
 * uses.
 */
export type OrderAccount = Pick<BrokerConnectionDto, 'id' | 'label' | 'mode'>

export async function placeOrder(
  order: ClickOrder,
  connection: OrderAccount | null,
  newRequestId: () => string = makeRequestId,
  /**
   * Injected so this function does not read the wall clock.
   *
   * Without it every test of every other refusal in here passes or fails
   * depending on the time of day it is run — which is how the market-closed
   * guard arrived: it broke six unrelated tests the moment it was added.
   */
  now: () => Date = () => new Date(),
): Promise<PlaceResult> {
  /*
   * ───────────────────────────────────────────────────────────────────────────
   * THE EXCHANGE IS SHUT. SAY SO HERE, NOT AFTER A ROUND TRIP.
   *
   * Every one of these used to be sent, claimed, gated and refused by
   * FEED_STALE — the engine telling the trader that the price it was given
   * is too old to trade against. That is the correct refusal and the wrong
   * MOMENT for it: the screen already knows the market is closed, says so in
   * a banner above the ladder, and then lets the click through to learn it
   * again from the server a second later. It also leaves a REFUSED row in
   * the order book for something that was never going to be an order.
   *
   * SIMULATION IS EXEMPT, and that exemption is the whole reason this lives
   * at the chokepoint rather than on a button: a simulation session runs at
   * any hour, its feed is live by construction, and refusing by the clock
   * would make the product untestable outside market hours. The question
   * asked is therefore not "what time is it" but "is this order going to a
   * REAL exchange right now".
   *
   * HOLIDAYS ARE NOT COVERED. `isOrderWindow` knows weekends and hours,
   * and `marketWindow.ts` is explicit that we hold no holiday calendar —
   * guessing one wrong in the other direction would refuse orders on a day
   * the market is open, which is far worse than one wasted round trip. On a
   * holiday the old behaviour stands and FEED_STALE still catches it.
   * ───────────────────────────────────────────────────────────────────────────
   */
  if (
    tickSocket.getMarketContext().source !== 'SIMULATION' &&
    !isOrderWindow(now())
  ) {
    return {
      ok: false,
      reason:
        'The market is closed. Orders can be placed between 09:15 and 15:40 IST.',
    }
  }

  if (!connection) return { ok: false, reason: 'Choose an account first.' }
  if (order.legs.length === 0) return { ok: false, reason: 'Nothing to send.' }
  if (order.legs.some((l) => !(l.lots > 0)))
    return { ok: false, reason: 'Set a size first.' }
  if (order.legs.some((l) => !(l.token > 0)))
    return { ok: false, reason: 'That strike is not listed.' }

  const clientRequestId = order.clientRequestId ?? newRequestId()
  const request: OrderIntentRequest = {
    clientRequestId,
    kind: 'ENTRY',
    // From the CONNECTION, never from the screen's own idea of the mode.
    mode: connection.mode,
    connectionId: connection.id,
    legs: order.legs.map((l) => ({
      token: l.token,
      side: l.side,
      lots: l.lots,
      orderType: 'MARKET',
    })),
    ...(order.premiumOrder ? { premiumOrder: order.premiumOrder } : {}),
  }

  orderStore.submitting(
    clientRequestId,
    order.label,
    Date.now(),
    {
      connectionId: connection.id,
      label: connection.label,
      mode:
        tickSocket.getMarketContext().source === 'SIMULATION'
          ? 'PAPER'
          : connection.mode,
    },
    order.premiumOrder?.limitPrice ?? null,
  )
  try {
    const ack = await tickSocket.submitIntent(request)
    if (
      !ack.ok &&
      /no answer|MAY have been recorded|no acknowledgement/i.test(
        ack.reason ?? '',
      )
    ) {
      orderStore.uncertain(
        clientRequestId,
        ack.reason ?? 'No acknowledgement. Check Orders.',
      )
    } else
      orderStore.acked(
        clientRequestId,
        ack.ok,
        ack.intentId ?? null,
        ack.reason,
      )

    /*
     * ── THE SECOND HALF OF A CROSS — ROADMAP 9.11 ───────────────────────────
     *
     * The server netted this click against a manual position and it went
     * through flat, so it was sent as two orders: this one closed the leg and
     * the reversal opens the other way.
     *
     * Registered HERE rather than left to arrive on its own. `applyEvent`
     * would create the row from the first execution event — it tolerates an
     * id this tab did not place — but then the order book shows one row for a
     * second and silently grows another, which reads as a duplicate order on
     * the one screen where that is the worst thing to suggest.
     */
    if (ack.ok && ack.reversal) {
      orderStore.submitting(
        ack.reversal.clientRequestId,
        `${order.label} (reverse)`,
        Date.now(),
        {
          connectionId: connection.id,
          label: connection.label,
          mode:
            tickSocket.getMarketContext().source === 'SIMULATION'
              ? 'PAPER'
              : connection.mode,
        },
        null,
      )
      orderStore.acked(
        ack.reversal.clientRequestId,
        true,
        ack.reversal.intentId,
      )
    }
    return ack.ok
      ? { ok: true, reason: null }
      : { ok: false, reason: ack.reason ?? 'The order was not accepted.' }
  } catch (e: unknown) {
    /*
     * A SEND THAT NEVER GOT AN ANSWER IS NOT A REFUSAL. The socket may have
     * dropped after the intent reached the server, so the order may exist.
     * The store is told it is unacknowledged rather than failed, and the
     * words sent to the trader say to check the book — §4.13's rule, that a
     * timeout must never be reported as "nothing happened".
     */
    orderStore.uncertain(
      clientRequestId,
      'No acknowledgement. Check Orders before retrying.',
    )
    return {
      ok: false,
      reason:
        e instanceof Error && e.message
          ? `No acknowledgement (${e.message}). Check the order book before retrying.`
          : 'No acknowledgement. Check the order book before retrying.',
    }
  }
}
